import React, { useEffect, useState } from 'react';
import {
    XCircle,
    CheckCircle,
    Clock,
    Mail,
    User,
    FileText,
    Paperclip,
    Send,
    ExternalLink,
    Calendar,
    Eye,
    Copy,
    AlertCircle,
    Loader2
} from 'lucide-react';
import { apiFetch } from '../api';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';

export default function EmailDetails({ email, onClose, setError, loginId, deliveryMode }) {
    const { client, connected } = useWebSocket();
    const [isBodyLoading, setIsBodyLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [showHtmlPreview, setShowHtmlPreview] = useState(false);
    const [emailBody, setEmailBody] = useState('');
    const [emailBodyData, setEmailBodyData] = useState('');

    const safeJson = async (response) => {
        try {
            return await response.json();
        } catch (e) {
            return null;
        }
    };

    const fetchEmailBodyById = async (emailId) => {
        if (!emailId) {
            setError('Missing email id');
            return;
        }

        setIsBodyLoading(true);
        setError('');

        try {
            if (deliveryMode === 'socket') {
                // --- WEBSOCKET LOGIC ---
                try {
                    // Ensure we are connected before asking for data
                    await ensureSocketConnection(client);
                } catch (connError) {
                    console.error(connError);
                    setError('Socket connection failed.');
                    setIsBodyLoading(false);
                    return;
                }

                // 1. Subscribe to the specific response topic
                // Based on your backend: sendToUserTopic(WebHook.GET_EMAIL_BODY_BY_ID...)
                // Standard pattern implies: /topic//get/email/body/{loginId}
                const topic = `/topic/get/email/body/${loginId}`;

                const subscription = client.subscribe(topic, (msg) => {
                    try {
                        // Parse response
                        const payload = JSON.parse(msg.body);
                        // Handle double-stringification if necessary
                        const data = (typeof payload === 'string') ? JSON.parse(payload) : payload;

                        // Reuse your existing robust extraction logic
                        let bodyHtml = null;
                        if (data) {
                            setEmailBodyData(data[' DATA ']);
                            if (data[' DATA '] && typeof data[' DATA '].body === 'string') {
                                bodyHtml = data[' DATA '].body;
                            } else if (data.DATA && typeof data.DATA.body === 'string') {
                                bodyHtml = data.DATA.body;
                            } else if (data.data && typeof data.data.body === 'string') {
                                bodyHtml = data.data.body;
                            } else if (data.Response && typeof data.Response.body === 'string') {
                                bodyHtml = data.Response.body;
                            } else if (typeof data.body === 'string') {
                                bodyHtml = data.body;
                            } else if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                                // Fallback for array response
                                const first = data.Response[0];
                                if (first && typeof first.body === 'string') bodyHtml = first.body;
                            }
                        }

                        if (bodyHtml) {
                            setEmailBody(bodyHtml);
                        } else {
                            setEmailBody('');
                            setError('Email body not found in socket response.');
                        }
                    } catch (e) {
                        console.error('Failed to parse socket email body', e);
                        setError('Failed to parse email body data.');
                    } finally {
                        // Unsubscribe immediately after receiving the single response
                        subscription.unsubscribe();
                        setIsBodyLoading(false);
                    }
                });

                // 2. Publish the request
                // Your backend signature is `getEmailBodyById(String emailId)`
                // Spring expects the raw body to be the string "emailId"
                client.publish({
                    destination: `/app/get/email/body`,
                    body: emailId // Sending the ID directly as string
                });

            } else {
                // --- API LOGIC (Existing) ---
                const url = `/get/email/body?emailId=${encodeURIComponent(emailId)}`;
                const res = await apiFetch(url);
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

                const data = await safeJson(res);

                let bodyHtml = null;
                if (data) {
                    setEmailBodyData(data[' DATA ']);
                    if (data[' DATA '] && typeof data[' DATA '].body === 'string') {
                        bodyHtml = data[' DATA '].body;
                    } else if (data.DATA && typeof data.DATA.body === 'string') {
                        bodyHtml = data.DATA.body;
                    } else if (data.data && typeof data.data.body === 'string') {
                        bodyHtml = data.data.body;
                    } else if (data.Response && typeof data.Response.body === 'string') {
                        bodyHtml = data.Response.body;
                    } else if (typeof data.body === 'string') {
                        bodyHtml = data.body;
                    } else if (data.STATUS && data.STATUS === 'SUCCESS' && Array.isArray(data.Response)) {
                        const first = data.Response[0];
                        if (first && typeof first.body === 'string') bodyHtml = first.body;
                    }
                }

                if (bodyHtml) {
                    setEmailBody(bodyHtml);
                } else {
                    setEmailBody('');
                    setError('Email body not found in response.');
                }
                // Mark loading false for API flow here
                setIsBodyLoading(false);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('Error fetching email body:', err);
            setError('Failed to load email body. Network error or API issue.');
            setEmailBody('');
            setIsBodyLoading(false);
        }
    };

    console.log("emailBody", emailBody);

    useEffect(() => {
        fetchEmailBodyById(email._id);
    }, [email]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'SUCCESS': return 'text-green-400';
            case 'FAILED': return 'text-red-400';
            default: return 'text-yellow-400';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'SUCCESS': return CheckCircle;
            case 'FAILED': return XCircle;
            default: return Clock;
        }
    };

    const getStatusBg = (status) => {
        switch (status) {
            case 'SUCCESS': return 'bg-green-500/20 border-green-500/30';
            case 'FAILED': return 'bg-red-500/20 border-red-500/30';
            default: return 'bg-yellow-500/20 border-yellow-500/30';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateStr;
        }
    };

    const parseEmailList = (emailString) => {
        if (!emailString) return [];
        return emailString.split(',').map(email => email.trim()).filter(Boolean);
    };

    const handleResendEmail = async () => {
        if (email.STATUS !== 'FAILED') {
            setError('Only failed emails can be resent');
            return;
        }

        setIsResending(true);
        try {
            // Prepare the resend payload
            const resendPayload = {
                to: email.to,
                subject: email.subject,
                body: emailBody,
                appName: email.appName,
                body_type: email?.body_type ? "Y" : "N",
                cc: email.cc ?? null,
                bcc: email.bcc ?? null,
                email_attachment: email.email_attachment ?? false,
                attachment_format: email.attachment_format ?? null,
                attachment_data: emailBodyData?.attachment_data ?? email.attachment_data ?? null,
                attachment_data_1: emailBodyData?.attachment_data_1 ?? email.attachment_data_1 ?? null,
                attachment_data_2: emailBodyData?.attachment_data_2 ?? email.attachment_data_2 ?? null,
                email_attachment_compress: email.email_attachment_compress ?? false
            };


            const response = await apiFetch('/email/send', {
                method: 'POST',
                body: JSON.stringify(resendPayload)
            });

            if (!response.ok) {
                throw new Error(`Failed to resend email: ${response.status}`);
            }

            const data = await response.json();

            if (data.STATUS === 'SUCCESS') {
                setError(''); // Clear any previous errors
                alert('Email resent successfully!');
                onClose(); // Close the modal after successful resend
            } else {
                throw new Error(data.message || 'Failed to resend email');
            }
        } catch (error) {
            console.error('Error resending email:', error);
            setError('Failed to resend email: ' + error.message);
        } finally {
            setIsResending(false);
        }
    };

    const escapeHtml = (unsafe) => {
        return unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    };

    const handleViewRawData = () => {
        // Escape HTML inside email.body
        const safeEmail = {
            ...email,
            body: emailBody ? escapeHtml(emailBody) : emailBody
        };

        const rawData = JSON.stringify(safeEmail, null, 2);

        const newWindow = window.open('', '_blank');
        newWindow.document.write(`
    <html>
      <head>
        <title>Raw Email Data - ${email._id}</title>
        <style>
          body {
            background: #1e293b;
            color: #e2e8f0;
            font-family: 'Courier New', monospace;
            padding: 20px;
            margin: 0;
          }
          pre {
            background: #0f172a;
            padding: 20px;
            border-radius: 8px;
            overflow-x: auto;
            border: 1px solid #334155;
            white-space: pre-wrap;
          }
          h1 {
            color: #10b981;
            margin-bottom: 20px;
          }
        </style>
      </head>
      <body>
        <h1>Raw Email Data</h1>
        <pre>${rawData}</pre>
      </body>
    </html>
  `);

        newWindow.document.close();
    };

    const fallbackCopy = (text) => {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
    };

    const handleCopyHtml = async () => {
        try {
            const escapedHtml = escapeHtml(emailBody || "");
            // Clipboard API available?
            if (!navigator?.clipboard?.writeText) {
                console.warn("Clipboard API not available, using fallback");
                fallbackCopy(escapedHtml);
                alert("HTML copied to clipboard!");
                return;
            }

            await navigator.clipboard.writeText(escapedHtml);
            alert("HTML copied to clipboard!");
        } catch (err) {
            console.error("Failed to copy HTML:", err);
            alert("Failed to copy HTML");
        }
    };

    const handlePreviewHtml = () => {
        if (!email.body_type || !emailBody) {
            setError('No HTML content available for preview');
            return;
        }
        setShowHtmlPreview(true);
    };

    if (!email) return null;

    return (
        <>
            {/* Main Modal */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4">
                <div className="bg-slate-900/95 backdrop-blur-xl border border-white/20 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/20 bg-white/5 sticky top-0 z-10">
                        <h3 className="text-xl sm:text-2xl font-bold text-white">Email Details</h3>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 sm:w-10 sm:h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-all duration-200"
                        >
                            <XCircle className="w-5 h-5 text-white" />
                        </button>
                    </div>

                    <div className="p-4 sm:p-6 space-y-6">
                        {/* Status and Basic Info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            {email?.rf1_id && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site Id</p>
                                    <p className="text-white text-sm font-mono break-all">{email.rf1_id}</p>
                                </div>
                            )}
                            {email?.rf1_name && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Site Name</p>
                                    <p className="text-white text-sm font-mono break-all">{email.rf1_name}</p>
                                </div>
                            )}
                            {email?.rf2_id && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location Id</p>
                                    <p className="text-white text-sm font-mono break-all">{email.rf2_id}</p>
                                </div>
                            )}
                            {email?.rf2_name && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-1">Location Name</p>
                                    <p className="text-white text-sm font-mono break-all">{email.rf2_name}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">ID</p>
                                <p className="text-white text-sm font-mono bg-white/10 p-2 rounded-lg break-all">{email._id}</p>
                            </div>
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Status</p>
                                <div className="flex items-center space-x-2">
                                    <div className={`px-4 py-2 rounded-full border ${getStatusBg(email.STATUS)} flex items-center space-x-2`}>
                                        {React.createElement(getStatusIcon(email.STATUS), {
                                            className: `w-5 h-5 ${getStatusColor(email.STATUS)}`
                                        })}
                                        <span className={`text-sm font-medium ${getStatusColor(email.STATUS)}`}>
                                            {email.STATUS}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Email Content */}
                        <div className="space-y-4">
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Subject</p>
                                <p className="text-white text-lg font-semibold bg-white/10 p-3 rounded-lg">
                                    {email.subject || 'No Subject'}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Application Name</p>
                                    <div className="flex items-center space-x-2 bg-white/10 p-3 rounded-lg">
                                        <User className="w-5 h-5 text-green-400" />
                                        <p className="text-white font-medium">{email.appName || 'N/A'}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Created</p>
                                    <div className="flex items-center space-x-2 bg-white/10 p-3 rounded-lg">
                                        <Clock className="w-5 h-5 text-blue-400" />
                                        <p className="text-white">{formatDate(email.creation_time_str)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recipients */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-semibold text-white border-b border-white/20 pb-2">Recipients</h4>

                            {/* To Recipients */}
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">To</p>
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                    <div className="flex flex-wrap gap-2">
                                        {parseEmailList(email.to).map((emailAddr, index) => (
                                            <div key={index} className="flex items-center space-x-2 bg-green-500/20 px-3 py-1 rounded-lg border border-green-500/30">
                                                <Mail className="w-4 h-4 text-green-400" />
                                                <span className="text-green-200 text-sm font-mono">{emailAddr}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* CC Recipients */}
                            {email.cc && parseEmailList(email.cc).length > 0 && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-2">CC</p>
                                    <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                        <div className="flex flex-wrap gap-2">
                                            {parseEmailList(email.cc).map((emailAddr, index) => (
                                                <div key={index} className="flex items-center space-x-2 bg-blue-500/20 px-3 py-1 rounded-lg border border-blue-500/30">
                                                    <Mail className="w-4 h-4 text-blue-400" />
                                                    <span className="text-blue-200 text-sm font-mono">{emailAddr}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* BCC Recipients */}
                            {email.bcc && parseEmailList(email.bcc).length > 0 && (
                                <div>
                                    <p className="text-white/40 text-sm uppercase tracking-wide mb-2">BCC</p>
                                    <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                        <div className="flex flex-wrap gap-2">
                                            {parseEmailList(email.bcc).map((emailAddr, index) => (
                                                <div key={index} className="flex items-center space-x-2 bg-purple-500/20 px-3 py-1 rounded-lg border border-purple-500/30">
                                                    <Mail className="w-4 h-4 text-purple-400" />
                                                    <span className="text-purple-200 text-sm font-mono">{emailAddr}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Email Body */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-white/40 text-sm uppercase tracking-wide">Message Body</p>
                                {email.body_type && emailBody && (
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={handleCopyHtml}
                                            className="flex items-center space-x-2 px-3 py-1 bg-green-500/20 text-green-200 rounded-lg hover:bg-green-500/30 transition-all duration-200 text-sm"
                                        >
                                            <Copy className="w-4 h-4" />
                                            <span>Copy HTML</span>
                                        </button>
                                        <button
                                            onClick={handlePreviewHtml}
                                            className="flex items-center space-x-2 px-3 py-1 bg-blue-500/20 text-blue-200 rounded-lg hover:bg-blue-500/30 transition-all duration-200 text-sm"
                                        >
                                            <Eye className="w-4 h-4" />
                                            <span>Preview HTML</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                {email.body_type && emailBody ? (
                                    <div
                                        className="text-white leading-relaxed text-sm max-h-[800px] overflow-y-auto"
                                        dangerouslySetInnerHTML={{ __html: emailBody }}
                                    />
                                ) : (
                                    <div className="flex items-start space-x-3">
                                        <FileText className="w-5 h-5 text-white/60 flex-shrink-0 mt-1" />
                                        <div className="flex-1">
                                            <p className="text-white leading-relaxed whitespace-pre-wrap text-sm max-h-[800px] overflow-y-auto">
                                                {emailBody || 'No message body provided'}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Attachments */}
                        {(email.email_attachment || emailBodyData.attachment_data?.length > 0 || email.attachment_data?.length > 0) && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Attachments</p>
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4 space-y-3">
                                    {email.attachment_format && (
                                        <div className="flex items-center justify-between p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                                            <div className="flex items-center space-x-3">
                                                <Paperclip className="w-5 h-5 text-blue-400" />
                                                <div>
                                                    <p className="text-blue-200 font-medium">Attachment Format</p>
                                                    <p className="text-blue-300 text-sm uppercase">{email.attachment_format}</p>
                                                </div>
                                            </div>
                                            {email.email_attachment_compress && (
                                                <span className="bg-blue-500/30 text-blue-200 px-3 py-1 rounded-full text-sm font-medium">
                                                    Compressed
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Attachment Data */}
                                    {(emailBodyData.attachment_data?.length > 0 || email.attachment_data?.length > 0) && (() => {
                                        const attachmentData =
                                            emailBodyData.attachment_data?.length > 0
                                                ? emailBodyData.attachment_data
                                                : email.attachment_data;

                                        return (
                                            <div>
                                                <h5 className="text-white font-medium mb-2">
                                                    Attachment Data ({attachmentData.length} records)
                                                </h5>
                                                <div className="max-h-48 overflow-y-auto space-y-2">
                                                    {attachmentData.map((data, index) => (
                                                        <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-3">
                                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                                                                {Object.entries(data ?? {}).map(([key, value]) => (
                                                                    <div key={key}>
                                                                        <span className="text-white/60 capitalize">{key}:</span>
                                                                        <span className="text-white ml-2">
                                                                            {value ?? "N/A"}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Additional Attachment Data */}
                                    {(emailBodyData.attachment_data_1?.length > 0 || email.attachment_data_1?.length > 0) && (() => {
                                        const attachmentData =
                                            emailBodyData.attachment_data_1?.length > 0
                                                ? emailBodyData.attachment_data_1
                                                : email.attachment_data_1;

                                        return (
                                            <div>
                                                <h5 className="text-white font-medium mb-2">
                                                    Attachment Data One ({attachmentData.length} records)
                                                </h5>
                                                <div className="max-h-48 overflow-y-auto space-y-2">
                                                    {attachmentData.map((data, index) => (
                                                        <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-3">
                                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                                                                {Object.entries(data ?? {}).map(([key, value]) => (
                                                                    <div key={key}>
                                                                        <span className="text-white/60 capitalize">{key}:</span>
                                                                        <span className="text-white ml-2">
                                                                            {value ?? "N/A"}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {(emailBodyData.attachment_data_2?.length > 0 || email.attachment_data_2?.length > 0) && (() => {
                                        const attachmentData =
                                            emailBodyData.attachment_data_2?.length > 0
                                                ? emailBodyData.attachment_data_2
                                                : email.attachment_data_2;

                                        return (
                                            <div>
                                                <h5 className="text-white font-medium mb-2">
                                                    Attachment Data Two ({attachmentData.length} records)
                                                </h5>
                                                <div className="max-h-48 overflow-y-auto space-y-2">
                                                    {attachmentData.map((data, index) => (
                                                        <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-3">
                                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                                                                {Object.entries(data ?? {}).map(([key, value]) => (
                                                                    <div key={key}>
                                                                        <span className="text-white/60 capitalize">{key}:</span>
                                                                        <span className="text-white ml-2">
                                                                            {value ?? "N/A"}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* Server Response */}
                        {email.email_response && (
                            <div>
                                <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Server Response</p>
                                <div className={`${email.STATUS === 'FAILED' ? 'bg-red-500/20 border-red-500/30' : 'bg-green-500/20 border-green-500/30'} border rounded-lg p-4`}>
                                    <div className="flex items-start space-x-3">
                                        {email.STATUS === 'FAILED' ? (
                                            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                        ) : (
                                            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                        )}
                                        <div>
                                            <p className={`${email.STATUS === 'FAILED' ? 'text-red-200' : 'text-green-200'} font-medium mb-1`}>Response Status</p>
                                            <p className={`${email.STATUS === 'FAILED' ? 'text-red-300' : 'text-green-300'}`}>{email.email_response}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Timestamp Details */}
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Timestamp Information</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Calendar className="w-4 h-4 text-blue-400" />
                                        <p className="text-white/60 text-sm">Date</p>
                                    </div>
                                    <p className="text-white font-mono">{email.date}</p>
                                </div>
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Clock className="w-4 h-4 text-green-400" />
                                        <p className="text-white/60 text-sm">Creation Time</p>
                                    </div>
                                    <p className="text-white font-mono text-sm">{email.creation_time_str}</p>
                                </div>
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Clock className="w-4 h-4 text-purple-400" />
                                        <p className="text-white/60 text-sm">Creation Time (ms)</p>
                                    </div>
                                    <p className="text-white font-mono text-sm">{email.creation_time_ms}</p>
                                </div>
                            </div>
                        </div>

                        {/* Client Information */}
                        <div>
                            <p className="text-white/40 text-sm uppercase tracking-wide mb-2">Client Information</p>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <AlertCircle className="w-4 h-4 text-orange-400" />
                                        <p className="text-white/60 text-sm">Client IP</p>
                                    </div>
                                    <p className="text-white font-mono text-sm">{email.client_ip}</p>
                                </div>
                                {email.user_agent && (
                                    <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <AlertCircle className="w-4 h-4 text-orange-400" />
                                            <p className="text-white/60 text-sm">User Agent</p>
                                        </div>
                                        <p className="text-white font-mono text-xs break-all">{email.user_agent}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Modal Actions */}
                    <div className="flex flex-col sm:flex-row items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3 p-4 sm:p-6 border-t border-white/20 bg-white/5 sticky bottom-0">
                        <button
                            onClick={onClose}
                            className="w-full sm:w-auto px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200"
                        >
                            Close
                        </button>
                        {email.STATUS === 'FAILED' && (
                            <button
                                onClick={handleResendEmail}
                                disabled={isResending}
                                className="w-full sm:w-auto px-6 py-2 bg-green-500/20 text-green-200 rounded-lg hover:bg-green-500/30 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isResending ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Resending...</span>
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        <span>Resend Email</span>
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            onClick={handleViewRawData}
                            className="w-full sm:w-auto px-6 py-2 bg-blue-500/20 text-blue-200 rounded-lg hover:bg-blue-500/30 transition-all duration-200 flex items-center justify-center space-x-2"
                        >
                            <ExternalLink className="w-4 h-4" />
                            <span>View Raw Data</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* HTML Preview Modal */}
            {showHtmlPreview && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-3 sm:p-4">
                    <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
                        {/* Preview Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-xl font-bold text-gray-800">HTML Email Preview</h3>
                            <button
                                onClick={() => setShowHtmlPreview(false)}
                                className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-all duration-200"
                            >
                                <XCircle className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>

                        {/* Preview Content */}
                        <div className="flex-1 overflow-auto bg-gray-100 p-4">
                            <div className="bg-white rounded-lg shadow-lg">
                                <iframe
                                    srcDoc={emailBody}
                                    title="Email HTML Preview"
                                    className="w-full h-[70vh] border-0 rounded-lg"
                                    sandbox="allow-same-origin"
                                />
                            </div>
                        </div>

                        {/* Preview Actions */}
                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowHtmlPreview(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}